import { aperture } from 'ramda';
import { DateAsNumber } from 'src/types/date';

export type DateInterval = { start: Date; end: Date };

export type TimeWindow = [DateAsNumber, DateAsNumber];

export type DateIntervalGroup<T> = DateInterval & { items: T[] };

export const getTimeWindowFromInterval = ({
  start,
  end,
}: DateInterval): TimeWindow => [+start, +end];

/**
 * Given a set of records, this function creates a set
 * of continuous time intervals using the start and end timestamps of the
 * provided data. It then identifies the records that coincide at each
 * time interval. For time intervals with no records it returns an empty array.
 * @param items
 * @param additonalTimestamps - Optional: A set of ordered timestamps, for enhancing time windows.
 * @param timeHorizon - Optional: The time horizon that dictates the start
 * and the end of the set of time windows
 * @returns An array of records grouped by time windows.
 */
export const getGroupedByContinuousDateIntervals = <T extends DateInterval>({
  items,
  additionalTimestamps,
  timeHorizon,
}: {
  items: T[];
  additionalTimestamps?: DateAsNumber[];
  timeHorizon?: DateInterval;
}): DateIntervalGroup<T>[] => {
  const sortedUniqueTimestamps = Array.from(
    new Set([
      ...items.flatMap(getTimeWindowFromInterval),
      ...(additionalTimestamps ? additionalTimestamps : []),
      ...(timeHorizon ? getTimeWindowFromInterval(timeHorizon) : []),
    ]),
  ).sort((a, b) => a - b);

  const boundedSortedTimestamps = timeHorizon
    ? sortedUniqueTimestamps.filter(
        (ts) => ts >= +timeHorizon.start && ts <= +timeHorizon.end,
      )
    : sortedUniqueTimestamps;

  const timeWindowSets: TimeWindow[] = aperture(2, boundedSortedTimestamps);

  // Stack availabilities at each timeWindow
  const result: DateIntervalGroup<T>[] = timeWindowSets.map(([start, end]) => {
    const matchingItems = items.filter(
      (it) => +it.start < end && +it.end > start,
    );

    return {
      start: new Date(start),
      end: new Date(end),
      items: matchingItems,
    };
  });

  return result;
};
